import { Tooltip as MuiTooltip } from '@mui/material';
import React from 'react';

import { Wrapper } from '@/shared/components/ui/tooltip/tooltip.styled';
import { TTooltipProps } from '@/shared/components/ui/tooltip/tooltip.types';

// Bounded despite the name: popper can only flip a tooltip into view if it fits
// beside its anchor, and 28rem does even at the 1024px `xs` breakpoint.
const WIDE_MAX_WIDTH = '28rem';

// Keeps the bubble off the viewport edge without padding the popper's
// interactive box, which would swallow clicks on whatever sits around it.
const VIEWPORT_PADDING_PX = 32;

const POPPER_MODIFIERS = [
  { name: 'preventOverflow', options: { padding: VIEWPORT_PADDING_PX } },
  { name: 'flip', options: { padding: VIEWPORT_PADDING_PX } },
];

export const Tooltip: React.FC<TTooltipProps> = ({
  wrapper,
  noMaxWidth,
  textAlign,
  children,
  clicked: _,
  ...props
}) => {
  // The wide variant is the one that wraps, and centred prose reads badly.
  const align = textAlign ?? (noMaxWidth ? 'left' : 'center');

  return (
    <MuiTooltip
      {...props}
      slotProps={{
        popper: { popperOptions: { modifiers: POPPER_MODIFIERS } },
        tooltip: {
          sx: {
            maxWidth: noMaxWidth ? WIDE_MAX_WIDTH : '300px',
            // 8rem, not 4rem: the viewport padding and the anchor offset sit
            // outside max-height, and 100vh counts the scrollbar gutter.
            maxHeight: 'min(300px, calc(100vh - 8rem))',
            overflow: 'auto',
            backgroundColor: (theme) => theme.palette.background.tooltip,
            padding: '0.25rem 0.75rem',
            borderRadius: (theme) => theme.shape.tooltipRadius,
            fontSize: (theme) => theme.typography.p7.fontSize,
            fontWeight: (theme) => theme.typography.p7.fontWeight,
            lineHeight: (theme) => theme.typography.p7.lineHeight,
            boxShadow: (theme) => theme.shadow.tooltip,
            color: (theme) => theme.palette.primary.contrastText,
            textWrap: 'balance',
            textAlign: align,
            '.custom-tag': {
              alignSelf: 'flex-start',
            },
          },
        },
        arrow: {
          sx: {
            color: (theme) => theme.palette.background.tooltip,
            '&::before': {
              borderRadius: (theme) => theme.shape.tooltipArrowRadius,
            },
          },
        },
      }}
    >
      {wrapper ? <Wrapper $wrapper={wrapper}>{children}</Wrapper> : children}
    </MuiTooltip>
  );
};
